import { collection, addDoc, query, where, getDocs, Timestamp, onSnapshot, doc, getDoc, updateDoc, orderBy, limit, deleteDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useNotificationStore } from '../store/notificationStore';

export interface SystemNotification {
  id?: string;
  title: string;
  message: string;
  type: 'info' | 'warning' | 'success' | 'error';
  imageUrl?: string;
  link?: string;
  createdAt: Timestamp;
  expiresAt?: Timestamp;
  isAdmin?: boolean;
  userId?: string | null;
  readBy: string[];
}

interface CreateNotificationParams {
  title: string;
  message: string;
  type: 'info' | 'warning' | 'success' | 'error';
  imageUrl?: string;
  link?: string;
  expiresAt?: Timestamp;
  isAdmin?: boolean;
  userId?: string | null;
}

const MAX_NOTIFICATIONS_PER_USER = 50;

const validateNotificationData = (data: CreateNotificationParams): boolean => {
  if (!data.title || typeof data.title !== 'string' || data.title.trim().length === 0) {
    console.error('Invalid title');
    return false;
  }

  if (!data.message || typeof data.message !== 'string' || data.message.trim().length === 0) {
    console.error('Invalid message');
    return false;
  }

  if (data.imageUrl && typeof data.imageUrl !== 'string') {
    console.error('Invalid imageUrl');
    return false;
  }

  if (data.link && typeof data.link !== 'string') {
    console.error('Invalid link');
    return false;
  }

  if (data.isAdmin && typeof data.isAdmin !== 'boolean') {
    console.error('Invalid isAdmin flag');
    return false;
  }

  return true;
};

export const createSystemNotification = async (params: CreateNotificationParams) => {
  try {
    if (!validateNotificationData(params)) {
      throw new Error('Invalid notification data');
    }

    const notificationData: Omit<SystemNotification, 'id'> = {
      title: params.title.trim(),
      message: params.message.trim(),
      type: params.type,
      createdAt: Timestamp.now(),
      readBy: [],
      isAdmin: params.isAdmin || false,
      userId: params.userId || null
    };

    if (params.imageUrl?.trim()) {
      notificationData.imageUrl = params.imageUrl.trim();
    }

    if (params.link?.trim()) {
      notificationData.link = params.link.trim();
    }

    if (params.expiresAt) {
      notificationData.expiresAt = params.expiresAt;
    }

    const docRef = await addDoc(collection(db, 'systemNotifications'), notificationData);
    return docRef.id;
  } catch (error) {
    console.error('Error creating system notification:', error);
    throw error;
  }
};

export const subscribeToSystemNotifications = (userId: string, isAdmin: boolean = false) => {
  // Clear existing notifications first
  const { clearAll } = useNotificationStore.getState();
  clearAll();

  const notificationsRef = collection(db, 'systemNotifications');
  const now = Timestamp.now();

  const unsubscribes: (() => void)[] = [];

  // Keep track of processed notification IDs to avoid duplicates
  const processedIds = new Set<string>();

  // User-specific notifications
  const userQuery = query(
    notificationsRef,
    where('userId', '==', userId)
  );
  
  // Global notifications
  const globalQuery = query(
    notificationsRef,
    where('userId', '==', null)
  );

  // Admin notifications
  const adminQuery = query(
    notificationsRef,
    where('isAdmin', '==', true)
  );

  const handleSnapshot = (snapshot: any) => {
    snapshot.docChanges().forEach((change: any) => {
      if (change.type === 'added') {
        const data = change.doc.data();
        const notification = {
          id: change.doc.id,
          ...data
        } as SystemNotification;

        // Skip if we've already processed this notification
        if (processedIds.has(notification.id)) {
          return;
        }

        // Add to processed IDs
        processedIds.add(notification.id);
        
        // Check expiration in memory instead of query
        const isExpired = notification.expiresAt && notification.expiresAt.toDate() <= now.toDate();
        const isUnread = !notification.readBy.includes(userId);
        
        if (!isExpired && isUnread) {
          const { addNotification } = useNotificationStore.getState();
          addNotification({
            id: notification.id,
            type: notification.type,
            title: notification.title,
            message: notification.message,
            imageUrl: notification.imageUrl,
            isAdmin: notification.isAdmin,
            action: notification.link ? {
              label: 'Voir plus',
              onClick: () => {
                if (notification.link?.startsWith('http')) {
                  window.open(notification.link, '_blank');
                } else {
                  window.location.href = notification.link || '/';
                }
              }
            } : undefined
          });
        }
      }
    });
  };

  // Add listeners
  unsubscribes.push(onSnapshot(userQuery, handleSnapshot));
  unsubscribes.push(onSnapshot(globalQuery, handleSnapshot));
  if (isAdmin) {
    unsubscribes.push(onSnapshot(adminQuery, handleSnapshot));
  }

  // Return cleanup function that unsubscribes all listeners and clears processed IDs
  return () => {
    unsubscribes.forEach(unsubscribe => unsubscribe());
    processedIds.clear();
  };
};

export const markNotificationAsRead = async (notificationId: string, userId: string) => {
  try {
    const notificationRef = doc(db, 'systemNotifications', notificationId);
    const notificationDoc = await getDoc(notificationRef);
    
    if (notificationDoc.exists()) {
      const currentReadBy = notificationDoc.data().readBy || [];
      
      if (!currentReadBy.includes(userId)) {
        await updateDoc(notificationRef, {
          readBy: [...currentReadBy, userId]
        });
      }
    }
  } catch (error) {
    console.error('Error marking notification as read:', error);
    throw error;
  }
};

export const cleanupOldNotifications = async () => {
  try {
    const now = Timestamp.now();
    const expiredQuery = query(
      collection(db, 'systemNotifications'),
      where('expiresAt', '<=', now)
    );
    
    const snapshot = await getDocs(expiredQuery);
    const deletePromises = snapshot.docs.map(doc => deleteDoc(doc.ref));
    await Promise.all(deletePromises);
    
    return snapshot.size;
  } catch (error) {
    console.error('Error cleaning up old notifications:', error);
    throw error;
  }
};